import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Send, RefreshCw, AlertTriangle, Crown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Storage } from '../../lib/storage';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  checkRestrictions: () => Promise<{ canPost: boolean; reason?: string }>;
  publicarPost: (descripcion: string, fotos: string[]) => Promise<{ success: boolean; error?: string }>;
}

export function UploadModal({ isOpen, onClose, onSuccess, checkRestrictions, publicarPost }: UploadModalProps) {
  const [descripcion, setDescripcion] = useState('');
  const [fotos, setFotos] = useState<string[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const [comprimiendo, setComprimiendo] = useState(false);
  const [errorRestriction, setErrorRestriction] = useState<string | null>(null);

  // EFECTO: Limpiar y validar al abrir el modal
  useEffect(() => {
    if (isOpen) {
      setDescripcion('');
      setFotos([]);
      setErrorRestriction(null);
      validarAcceso();
    }
  }, [isOpen]);

  const validarAcceso = async () => {
    const restriction = await checkRestrictions();
    if (!restriction.canPost) {
      setErrorRestriction(restriction.reason || 'Error de restricciones');
    }
  };

  const procesarImagen = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = reject;
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target?.result as string;
          img.onerror = reject;
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const targetWidth = 1024;
              const targetHeight = 768; 
              canvas.width = targetWidth;
              canvas.height = targetHeight;
              const ctx = canvas.getContext('2d');
              if (!ctx) {
                reject(new Error("Canvas context error"));
                return;
              }
              const imgRatio = img.width / img.height;
              const targetRatio = targetWidth / targetHeight;
              let sx, sy, sWidth, sHeight;
              if (imgRatio > targetRatio) {
                sHeight = img.height;
                sWidth = img.height * targetRatio;
                sx = (img.width - sWidth) / 2;
                sy = 0;
              } else {
                sWidth = img.width;
                sHeight = img.width / targetRatio;
                sx = 0;
                sy = (img.height - sHeight) / 2;
              }
              ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);
              canvas.toBlob((blob) => {
                if (blob) {
                  if (blob.size > 120 * 1024) {
                     canvas.toBlob((b2) => {
                       resolve(b2 || blob);
                     }, 'image/webp', 0.5);
                  } else {
                     resolve(blob);
                   }
                } else {
                  reject(new Error("Blob failed"));
                }
              }, 'image/webp', 0.7);
            } catch (err) { reject(err); }
          };
        };
      } catch (err) { reject(err); }
    });
  };

  const handleSubirFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setComprimiendo(true);
    
    // Aquí iría la lógica de compresión (reutilizando la de MotoMarket)
    // Por ahora simularemos la subida a Supabase Storage o usaremos base64 para el MVP
    // Pero lo ideal es usar el bucket 'muro_biker'
    
    const file = e.target.files[0];
    const fileName = `${Date.now()}-${Math.floor(Math.random() * 1000)}.webp`;
    const filePath = `posts/${fileName}`;

    try {
      const blob = await procesarImagen(file);

      let finalUrl = '';
      try {
        const { error: uploadError } = await supabase.storage
          .from('muro_biker')
          .upload(filePath, blob, { contentType: 'image/webp' });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('muro_biker')
          .getPublicUrl(filePath);
          
        finalUrl = publicUrl;
      } catch (uploadErr) {
        console.warn('Fallo de subida a Storage del Muro. Aplicando auto-curación con fallback de Base64...', uploadErr);
        // Fallback: Convertir la imagen WebP optimizada en Base64
        finalUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
      }

      setFotos(prev => [...prev, finalUrl]);
    } catch (err) {
      console.error('Error subiendo foto:', err);
    } finally {
      setComprimiendo(false);
    }
  };

  const handlePublicar = async () => {
    setSubiendo(true);
    setErrorRestriction(null);

    const restriction = await checkRestrictions();
    if (!restriction.canPost) {
      setErrorRestriction(restriction.reason || 'Error de restricciones');
      setSubiendo(false);
      return;
    }

    try {
      const result = await publicarPost(descripcion, fotos);

      if (!result.success) {
        throw new Error(result.error);
      }

      setDescripcion('');
      setFotos([]);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error publicando:', err);
      setErrorRestriction(err.message || 'Error al publicar');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-xl flex flex-col pt-12 p-6 overflow-y-auto"
        >
          <div className="flex justify-between items-start mb-8">
            <div className="flex flex-col">
              <h2 className="text-lg font-black text-white uppercase tracking-tighter leading-none mb-1">Nueva Publicación</h2>
              <span className="text-[8px] text-primary-cyan font-black uppercase tracking-[3px]">Muro Bike VIP</span>
            </div>
            <button onClick={onClose} className="w-8 h-8 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-slate-500">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {/* GALERÍA */}
            <div className="bg-slate-800/50 backdrop-blur-xl border border-white/10 rounded-3xl p-3">
              <label className="text-[9px] font-black text-primary-cyan uppercase tracking-widest mb-2 block">Fotos (Máx 3)</label>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                <input type="file" id="muro-upload" className="hidden" accept="image/*" onChange={handleSubirFoto} />
                <button 
                  onClick={() => document.getElementById('muro-upload')?.click()}
                  disabled={comprimiendo || fotos.length >= 3}
                  className="w-20 h-20 bg-slate-700/50 border-2 border-dashed border-white/10 rounded-2xl flex flex-col items-center justify-center gap-1 shrink-0 disabled:opacity-50"
                >
                  {comprimiendo ? <RefreshCw className="w-5 h-5 text-primary-cyan animate-spin" /> : <Camera className="w-5 h-5 text-slate-500" />}
                </button>
                {fotos.map((url, idx) => (
                  <div key={idx} className="relative w-20 h-20 shrink-0">
                    <img src={url} className="w-full h-full object-cover rounded-2xl border border-white/10" />
                    <button onClick={() => setFotos(f => f.filter(u => u !== url))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* DESCRIPCIÓN */}
            <div className="bg-slate-800/50 backdrop-blur-xl border border-white/10 rounded-3xl p-3">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2 block">¿Qué estás pensando?</label>
              <textarea 
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
                placeholder="Comparte algo con la comunidad..."
                className="w-full bg-transparent text-white text-sm font-medium min-h-[100px] outline-none placeholder:text-slate-700 resize-none"
              />
            </div>

            {errorRestriction && (
              <div className="bg-red-500/10 border border-red-500/40 p-4 rounded-3xl flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                  <p className="text-[12px] text-red-500 font-black uppercase tracking-tight">{errorRestriction}</p>
                </div>
                
                {errorRestriction.toUpperCase().includes('LÍMITE') && (
                  <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-3xl flex flex-col items-center text-center gap-3 mt-2 shadow-xl shadow-amber-900/10">
                    <p className="text-[13px] text-amber-100 font-medium leading-tight flex items-center justify-center gap-1.5 flex-wrap">
                      ¡Sube de nivel! Los usuarios <span className="text-amber-400 font-black flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> PREMIUM</span> pueden compartir sus fotos <span className="text-white font-black underline decoration-amber-500">SIN LÍMITES</span>.
                    </p>
                    <button 
                      onClick={() => {
                        onClose(); // Cerramos el modal de subida
                        document.dispatchEvent(new CustomEvent('abrirMembresiaOro'));
                      }} 
                      className="px-6 py-2 bg-amber-500 text-bg-dark rounded-xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg shadow-amber-500/20"
                    >
                      Hazte Premium Oro
                    </button>
                  </div>
                )}
              </div>
            )}

            <button 
              onClick={handlePublicar}
              disabled={subiendo || !descripcion || fotos.length === 0}
              className="w-full py-3 bg-primary-cyan text-bg-dark rounded-2xl flex items-center justify-center gap-2 font-black text-xs uppercase tracking-widest disabled:opacity-50 transition-all active:scale-95 shadow-xl shadow-primary-cyan/20"
            >
              {subiendo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Publicar en el Muro</>}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
